



























































































































































































import {
  InstrumentDetail,
  InstrumentPerformanceData, RiskAndVolatilityMeasures,
} from "@/shared/types";
import { Component, Prop, Vue } from "@/shared/vuepack";
import moment from "moment";
import _ from "lodash";
import { roundToTwoDecimals } from "@/shared/mathHelper";
import { formatCurrency } from "@/shared/currency";
import { useDrivewealthStore } from "@/shared/store/drivewealth";
import { useInstrumentPerformanceDataStore } from "@/shared/store/instrumentPerformanceData";

enum CountryAllocationTableType {
  Allocation,
  Country
}

enum TimePeriod {
  OneYear = "M12",
  ThreeYear = "M36",
  FiveYear = "M60"
}

@Component({
  computed: {
    CountryAllocationTableType () {
      return CountryAllocationTableType;
    }
  }
})
export default class EtfPerformanceSection extends Vue {
  @Prop({ required: false }) username: string;
  @Prop({ required: false }) partnerId: number;
  @Prop() symbol: string;
  @Prop() accountId: number;

  moment = moment;
  formatCurrency = formatCurrency;
  roundToTwoDecimals = roundToTwoDecimals;

  instrumentPerformanceDataStore = useInstrumentPerformanceDataStore();
  drivewealthStore = useDrivewealthStore();

  collapseExpanded = false;
  selectedCountryAllocationTable = CountryAllocationTableType.Allocation;
  selectedTimePeriod = TimePeriod.OneYear;

  countryAllocationTableOptions = [
    {
      text: "Asset Allocation",
      value: CountryAllocationTableType.Allocation,
    },
    {
      text: "Country",
      value: CountryAllocationTableType.Country,
    }
  ];

  timePeriodOptions = [
    {
      text: "1Y",
      value: TimePeriod.OneYear,
    },
    {
      text: "3Y",
      value: TimePeriod.ThreeYear,
    },
    {
      text: "5Y",
      value: TimePeriod.FiveYear,
    }
  ];

  get instrumentDetail (): InstrumentDetail {
    return this.drivewealthStore.instrumentDetailBySymbol(this.symbol);
  }

  get instrumentPerformanceData (): InstrumentPerformanceData {
    return this.instrumentPerformanceDataStore.instrumentPerformanceData(this.symbol);
  }

  get displayStockSectorAllocationTable (): boolean {
    return this.stockSectorAllocations.length > 0;
  }

  get displayBondSectorAllocationTable (): boolean {
    return this.bondSectorAllocations.length > 0 && !this.displayStockSectorAllocationTable;
  }

  get stockSectorAllocationTableFields (): { key: string; label: string }[] {
    return [
      { key: "definition", label: "Sectors" },
      { key: "investmentPercentage", label: "Investment" }
    ];
  }

  get stockSectorAllocations () {
    const tableItems = this.instrumentPerformanceData.stockSectorBreakdown.map(x => {
      return {
        definition: x.definition,
        investmentPercentage: `${roundToTwoDecimals(x.investmentPercentage).toFixed(2)}%`,
      };
    });

    return _.take(tableItems, 10);
  }

  get bondSectorAllocations () {
    const tableItems = this.instrumentPerformanceData.bondSectorBreakdown.map(x => {
      return {
        definition: x.definition,
        investmentPercentage: `${roundToTwoDecimals(x.investmentPercentage).toFixed(2)}%`,
      };
    });

    return _.take(tableItems, 10);
  }

  get assetAllocationTableFields (): { key: string; label: string }[] {
    return [
      { key: "definition", label: "Asset Class" },
      { key: "investmentPercentage", label: "Investment %" }
    ];
  }

  get assetAllocations () {
    return this.instrumentPerformanceData.assetAllocation.map(x => {
      return {
        definition: x.definition,
        investmentPercentage: `${roundToTwoDecimals(x.investmentPercentage).toFixed(2)}%`,
      };
    });
  }

  get countryExposureTableFields (): { key: string; label: string }[] {
    return [
      { key: "definition", label: "Country" },
      { key: "investmentPercentage", label: "Investment %" }
    ];
  }

  get countryExposures () {
    return this.instrumentPerformanceData.countryExposure.map(x => {
      return {
        definition: x.definition,
        investmentPercentage: `${roundToTwoDecimals(x.investmentPercentage).toFixed(2)}%`,
      };
    });
  }

  get selectedRiskData (): RiskAndVolatilityMeasures {
    return _.find(this.instrumentPerformanceData.riskAndVolatilityMeasures, x => x.timePeriod === this.selectedTimePeriod);
  }

  get panelToggleCopy (): string {
    return this.collapseExpanded ? "- show less" : "+ show more";
  }

  togglePanelDisplay (): void {
    this.collapseExpanded = !this.collapseExpanded;
  }

  setSelectedCountryAllocationTableDisplay (tableType: CountryAllocationTableType): void {
    this.selectedCountryAllocationTable = tableType;
  }

  setSelectedTimePeriodTableDisplay (timePeriod: TimePeriod): void {
    this.selectedTimePeriod = timePeriod;
  }

  getIconStyleForIndex (index: number): string {
    const rating = this.parsedStarRating(this.instrumentPerformanceData.morningstarRating3Y);

    return index <= rating ? "fa-solid" : "fa-regular";
  }

  parsedStarRating (valueAsString: string): number {
    return parseInt(valueAsString) || 0;
  }
}
